import React, { useEffect, useMemo, useRef, useState } from "react";
import { SectionHeader } from "./SectionHeader";
/* ================= Brand Theme ================= */
const BRAND_VARS = `:root{ --brand-gold:#c99a05; --brand-blue:#0b2c5f; }`;
const scrollToWithOffset = (id, offset = 80) => { const element = document.getElementById(id); if (!element) return; const y = element.getBoundingClientRect().top + window.scrollY - offset; window.scrollTo({ top: y, behavior: "smooth" }); };

/* ================= Zeit-Helper ================= */
function nowInTZ(timeZone) {
  if (!timeZone) return new Date();
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
  return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`);
}
function startOfDayInTZ(year, month0, day, timeZone) {
  if (!timeZone) return new Date(year, month0, day, 0, 0, 0, 0);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(new Date(Date.UTC(year, month0, day))).map(p => [p.type, p.value])
  );
  return new Date(`${parts.year}-${parts.month}-${parts.day}T00:00:00`);
}
function millisUntilNextMidnight(now, timeZone) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const nextMidnight = startOfDayInTZ(y, m, d + 1, timeZone);
  return Math.max(0, nextMidnight - now);
}

/* =============== Utils =============== */
function shuffleArray(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ========= Kalender als Overlay über Bild ========= */
function AdventCalendarOverImage({
  data = [],
  timeZone = "Europe/Amsterdam",
  period,                                  // { year, month }
  imageSrc = "/img/calendartilly2026.jpg",
  title = "Adventskalender",
}) {
  const initialNow = nowInTZ(timeZone);
  const { year, month } = useMemo(
    () => period || { year: initialNow.getFullYear(), month: 11 },
    [period, timeZone]
  );

  const [currentNow, setCurrentNow] = useState(initialNow);
  const [openDay, setOpenDay] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    function scheduleMidnightTick(reference) {
      const ms = millisUntilNextMidnight(reference, timeZone);
      const id = setTimeout(() => setCurrentNow(nowInTZ(timeZone)), ms + 250);
      return () => clearTimeout(id);
    }
    setCurrentNow(nowInTZ(timeZone));
    const clearTimeoutFn = scheduleMidnightTick(nowInTZ(timeZone));
    const interval = setInterval(() => setCurrentNow(nowInTZ(timeZone)), 5 * 60 * 1000);
    return () => {
      clearTimeoutFn();
      clearInterval(interval);
    };
  }, [timeZone]);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (openDay !== null) {
      if (!dlg.open) dlg.showModal();
    } else if (dlg.open) {
      dlg.close();
    }
  }, [openDay]);

  /* === NEU: Reihenfolge nur EINMAL pro Monat generieren & persistieren === */
  const days = useMemo(() => {
    const key = `ak-order-${year}-${String(month + 1).padStart(2, "0")}`;
    const byTag = new Map();
    data.forEach(d => byTag.set(d.tag, d));

    // Grundlage 1..24
    const base = Array.from({ length: 24 }, (_, i) => byTag.get(i + 1) || { tag: i + 1, gewinne: [] });

    // lese persistierte Ordnung (nur Tags)
    let tagOrder = null;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 24 && parsed.every(n => Number.isInteger(n))) {
        tagOrder = parsed;
      }
    } catch {}

    // Erzeuge & speichere einmalig, falls keine Ordnung existiert
    if (!tagOrder) {
      tagOrder = shuffleArray(base.map(d => d.tag));
      try { localStorage.setItem(key, JSON.stringify(tagOrder)); } catch {}
    }

    // Map zurück auf Objekte
    const byTagObj = new Map(base.map(d => [d.tag, d]));
    return tagOrder.map(tag => byTagObj.get(tag) || { tag, gewinne: [] });
  }, [data, year, month]);

  // aktueller Tag (im gewählten Zeitraum)
  const currentCalDay = useMemo(() => {
    const sameMonth = currentNow.getFullYear() === year && currentNow.getMonth() === month;
    return sameMonth ? currentNow.getDate() : null;
  }, [currentNow, year, month]);

  function isUnlocked(day) {
   
    return true;
  }

  return (
    <div className="ak-wrap">
      
      <style>{`
        ${BRAND_VARS}
        .ak-wrap{ width:100%; max-width:980px; margin:0 auto; }
        .ak-head{ display:flex; align-items:center; justify-content:space-between; gap:12px; margin: 10px 0 8px; }
        .ak-title{ font-weight:800; font-size:20px; color:var(--brand-blue); }
        .ak-sub{ font-size:13px; color:#555; }
        .ak-progress{ font-size:13px; color:#666; margin-bottom:8px; }

        .ak-figure{ position:relative; width:100%; border-radius:12px; overflow:hidden; margin: 0 0 18px; }
        .ak-img{ display:block; width:100%; height:auto; }

        .ak-figure.overlay{ margin: 0; }
        .ak-overlay{
          position:absolute; inset:0;
          display:grid; gap:4px; padding:6px;
          grid-template-columns: repeat(4, 1fr);
          grid-template-rows: repeat(6, 1fr);
          pointer-events:none;
        }
        @media (min-width:640px){
          .ak-overlay{
            gap:6px; padding:10px;
            grid-template-columns: repeat(6, 1fr);
            grid-template-rows: repeat(4, 1fr);
          }
        }

        .ak-tile{
          pointer-events:auto;
          display:flex; align-items:center; justify-content:center;
          border: 2px solid var(--brand-gold);
          background: rgba(255,255,255,0.35);
          backdrop-filter: blur(0.5px);
          color: var(--brand-gold);
          font-weight: 900;
          font-size: clamp(14px, 4.5vw, 24px);
          text-shadow: 0 0 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.8), 0 2px 2px rgba(0,0,0,0.85);
          border-radius:10px;
          min-height:0;
          position:relative;
          transition: transform .08s ease, background .2s ease, box-shadow .2s ease, opacity .2s ease, filter .2s ease, outline .2s ease;
        }

        /* === NEU: deutlichere Kennzeichnung für freigeschaltete Türchen === */
        .ak-tile.unlocked{
          cursor:pointer;
          background: linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.2));
          box-shadow: inset 0 0 0 1px rgba(201,154,5,.35), 0 4px 14px rgba(0,0,0,.25);
          filter: none;
        }
        .ak-tile.unlocked:hover{
          transform: translateY(-2px) scale(1.015);
          box-shadow: inset 0 0 0 1px rgba(201,154,5,.55), 0 10px 24px rgba(0,0,0,0.32);
          background: linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.35));
        }
        /* kleines "offen"-Badge */
        .ak-tile.unlocked::after{
          content: "🔓";
          position:absolute; top:6px; right:8px; font-size:14px;
          text-shadow:none;
        }

        /* Gesperrt: stärker entsättigt */
        .ak-tile.locked{
          opacity:.45;
          filter: grayscale(0.2) contrast(0.9);
          cursor:not-allowed;
        }

        /* === NEU: Heutiger Tag extra hervorheben (Puls + Badge) === */
        @keyframes akPulse {
          0% { box-shadow: 0 0 0 0 rgba(201,154,5,0.55); }
          70% { box-shadow: 0 0 0 10px rgba(201,154,5,0); }
          100% { box-shadow: 0 0 0 0 rgba(201,154,5,0); }
        }
        .ak-tile.current{
          outline: 3px solid var(--brand-gold);
          background: rgba(255,255,255,0.5);
          animation: akPulse 1.8s infinite;
        }
       .ak-tile.current::before{
  position:absolute;
  left:8px;
  top:6px;
  font-size:11px;
  font-weight:800;
  letter-spacing:.4px;
  color:#fff;
  background: var(--brand-blue);
  border:1px solid rgba(255,255,255,.7);
  padding:2px 6px;
  border-radius:999px;
  text-shadow:none;
}

@media (max-width: 480px){
  .ak-tile.current::before{
    font-size:9px;
    padding:1px 4px;
    top:4px;
    left:6px;
  }
}

        /* ===== Modal ===== */
        dialog.ak-modal{ border: none; padding: 0; background: transparent; max-width: unset; }
        .ak-card{ width: min(600px, 94vw); background: #fff; color: var(--brand-blue); border-radius: 20px; overflow: hidden; box-shadow: 0 28px 90px rgba(0,0,0,.35); }
        .ak-card-head{ position: relative; background: var(--brand-blue); color: #fff; padding: 16px 18px; border-bottom: 5px solid var(--brand-gold); text-align: center; }
        .ak-card-title{ font-size: 22px; font-weight: 800; letter-spacing: 0.5px; }
        .ak-close{ position: absolute; top: 12px; right: 14px; width: 38px; height: 38px; border-radius: 999px; display: grid; place-items: center; border: 2px solid rgba(255,255,255,.6); background: transparent; color: #fff; font-size: 24px; font-weight: 700; cursor: pointer; line-height: 1; }
        .ak-close:hover{ background: rgba(255,255,255,.18); }
        .ak-card-body{ padding: 22px 24px; font-size: 16px; line-height: 1.2; color: #223; }
        .ak-hint{ font-size: 16px; color: #445; margin-bottom: 14px; }
        .ak-list{ list-style: none; padding: 0; margin: 6px 0 0; display: grid; gap: 6px; }
        .ak-row{ border: 1px solid rgba(11,44,95,.25); background: #f9fbff; border-radius: 12px; padding: 7px 14px; display: flex; justify-content: space-between; align-items: center; }
        .ak-row .label{ font-size: 16px; font-weight: 700; color: var(--brand-blue); }
        .ak-row code{ font-size: 16px; font-weight: 700; color: var(--brand-gold); }
      
      .feature-logo {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
}

.feature-logo img {
    max-height: 80px;
    max-width: 160px;
    object-fit: contain;
}
      `}
      </style>
{/* Hinweis unter dem Kalender */}

      {/* TEIL 2: Bild mit Türchen-Overlay */}
      <figure className="ak-figure overlay">
        
        <img className="ak-img" src={imageSrc} alt="Weihnachtsmotiv mit Türchen" />
        {/* <div className="ak-overlay" aria-label="Türchen-Overlay">
          {days.map((day) => {
            const unlocked = true;
            const isCurrent = day.tag === currentCalDay;
            return (
              <button
                key={day.tag}
                className={[
                  "ak-tile",
                  unlocked ? "unlocked" : "locked",
                  isCurrent ? "current" : ""
                ].join(" ")}
                disabled={!unlocked}
                onClick={() => unlocked && setOpenDay(day.tag)}
                title={unlocked ? "öffnen" : "gesperrt"}
                aria-label={`Tag ${day.tag} ${isCurrent ? "(heute)" : ""} ${unlocked ? "– geöffnet werden kann" : "– noch gesperrt"}`}
              >
                {day.tag}
              </button>
            );
          })}
        </div> */}
      </figure>

      {/* Modal */}
      <dialog
        ref={dialogRef}
        className="ak-modal"
        onClick={(e) => {
          const dlg = e.currentTarget;
          const rect = dlg.getBoundingClientRect();
          const inside = (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom);
          if (!inside) setOpenDay(null);
        }}
      >
        <div className="ak-card" onClick={(e) => e.stopPropagation()}>
          <div className="ak-card-head">
            <div className="ak-card-title">Tag {openDay}</div>
            <button className="ak-close" aria-label="Schließen" onClick={() => setOpenDay(null)}>×</button>
          </div>
          <div className="ak-card-body">
            <div className="ak-hint">
              {openDay && isUnlocked(openDay)
                ? <>Gewinn-Nummern für den {openDay}. {new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(year, month, 1))} {year}</>
                : <>Dieser Tag ist noch gesperrt.</>}
            </div>

            {openDay && isUnlocked(openDay) ? (
              (data.find(d => d.tag === openDay)?.gewinne || []).length ? (
                <ul className="ak-list">
                  {data.find(d => d.tag === openDay).gewinne.map((g, idx) => (
                    <li key={idx} className="ak-row">
                      <span className="label">{g.titel}</span>
                      <code>{g.nummer}</code>
                    </li>
                  ))}
                </ul>
              ) : (
                <div>Keine Gewinne hinterlegt.</div>
              )
            ) : (
              <div>Komme ab 0:00 wieder – dann wird der Tag automatisch freigeschaltet.</div>
            )}
          </div>
        </div>
      </dialog>

      <div style={{ marginTop: 10, fontSize: 12, color: "#555" }}>
        Zeitraum: 1.–24. {new Intl.DateTimeFormat(undefined, { timeZone, month: "long" }).format(new Date(year, month, 1))} {year} • Zeitzone: {timeZone}
      </div>
    </div>
  );
}

/* ========= Deine Seite/Section ========= */
export const Features = ({ data, verkaufsstellen  }) => {
  if (!Array.isArray(data)) return null;
  return (
    <div id="adventskalender" className="text-center">
      <SectionHeader />
      <style>{BRAND_VARS}</style>
      <div className="container-fluid">
        <div className="col-md-10 col-md-offset-1 section-title">
          <h2 style={{ color: "var(--brand-blue)" }}>Adventskalender 2026</h2>
          <div style={{
  textAlign: "center",
  fontSize: "16px",
  color: "var(--brand-blue)",
  fontWeight: 600,
  marginBottom: "1px",
  marginTop: "4px"
}}>
  {/* <div class="rules-title">
  Abhlung noch möglich bis zum 14 Februar</div>
  <div class="rules-title">unter Vorlage der Kalender-Nummer bei: Mohnfeld Moden, Hohe Str. 21-23, 40213 Düsseldorf.</div>
   
  Habe ich gewonnen? Klicken auf das Türchen um es zu öffnen. */}
</div>
        </div>
        
        <AdventCalendarOverImage
          data={data}
          timeZone="Europe/Amsterdam"
          imageSrc="/img/calendartilly2026.jpg"
          // period={{ year: 2025, month: 11 }}
        />
         <SectionHeader />
        <h2 style={{ color: "var(--brand-blue)" }}>Verkaufsstellen des Düsseldorfer SI-Adventskalenders 2026</h2>
        <h3>Hier erhalten Sie den Düsseldorfer SI-Adventskalender 2026</h3>
<h3>Mit dem Kauf unseres Adventskalenders unterstützen Sie Projekte zur Förderung von
Mädchen und jungen Frauen in Düsseldorf.</h3>
    <div className="features-grid">
  {verkaufsstellen.map((stelle) => (
    <div key={stelle.name} className="feature-card">

      {stelle.logo && (
        <div className="feature-logo">
          <img src={stelle.logo} alt={`${stelle.name} Logo`} />
        </div>
      )}

      <h4>{stelle.name}</h4>
      <h3>{stelle.adresse}</h3>
      <h5>{stelle.plz}</h5>

      {stelle.email && (
        <p>
          <a href={`mailto:${stelle.email}`}>
            📧 {stelle.email}
          </a>
        </p>
      )}

      {stelle.website && (
        <p>
          <a
            href={stelle.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            🌐 Website
          </a>
        </p>
      )}

      {(stelle.adresse && stelle.plz) && (
        <p>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${stelle.adresse}, ${stelle.plz}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            📍 Google Maps
          </a>
        </p>
      )}

    </div>
  ))}

 {/*  {data.map((feature) => (
    <div key={feature.tag} className="feature-card">
      <h3>Tag {feature.tag}</h3>

      <ul>
        {feature.gewinne.map((gewinn, index) => (
          <li key={index}>
            <strong>{gewinn.titel}:</strong> {gewinn.nummer}
          </li>
        ))}
      </ul>
    </div>


  ))} */}
</div>
<h3>Weitere Verkaufsstellen werden laufend ergänzt.</h3>
<h3>Möchten auch Sie den SI-Adventskalender kaufen, verkaufen oder als Verkaufsstelle unterstützen?
 <a href={`mailto:adventskalender@siduesseldorf.de`}> adventskalender@siduesseldorf.de</a></h3>
      </div>
      
    </div>
  );
};
